import { useState, useEffect, useCallback } from "react";
import { Bell, Check, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "./adminApi";

export default function RemindersTab({ onNavigateToCandidates }) {
  const [reminders, setReminders] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/reminders" + (filter !== "all" ? `?status=${filter}` : ""));
      setReminders(r.data);
    } catch { toast.error("Impossible de charger les rappels"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/reminders/${id}`, { status });
      toast.success("Rappel mis à jour");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const deleteRem = async (id) => {
    if (!window.confirm("Supprimer ce rappel ?")) return;
    try { await api.delete(`/reminders/${id}`); toast.success("Supprimé"); fetchAll(); }
    catch { toast.error("Erreur"); }
  };

  const scanIdValidity = async () => {
    try {
      const r = await api.post("/admin/id-validity/scan");
      const n = r.data?.created || 0;
      toast.success(n > 0 ? `${n} nouveau${n > 1 ? "x" : ""} rappel(s) créé(s) pour pièces d'identité bientôt expirées` : "Aucune pièce d'identité bientôt expirée");
      if (n > 0) fetchAll();
    } catch { toast.error("Erreur lors du scan"); }
  };

  return (
    <div data-testid="reminders-tab" className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {[{ k: "pending", l: "En attente" }, { k: "done", l: "Terminés" }, { k: "snoozed", l: "Reportés" }, { k: "all", l: "Tous" }].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              className={`px-3 py-1.5 text-xs font-semibold border ${filter === f.k ? "bg-[#662483] text-white border-[#662483]" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"}`}
              data-testid={`reminder-filter-${f.k}`}>{f.l}</button>
          ))}
        </div>
        <button onClick={scanIdValidity} data-testid="scan-id-validity-btn"
          className="px-3 py-1.5 text-xs font-semibold border border-[#0069B4] text-[#0069B4] hover:bg-[#E6F0F7]">
          Scanner pièces d'identité
        </button>
      </div>
      {loading ? <p className="text-center py-12 text-gray-500">Chargement…</p>
        : reminders.length === 0 ? (
          <div className="bg-white border border-gray-200 p-8 text-center">
            <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-500">Aucun rappel {filter !== "all" ? `dans cette catégorie` : ""}.</p>
          </div>
        ) : (
        <div className="bg-white border border-gray-200 divide-y divide-gray-100">
          {reminders.map((r) => (
            <div key={r.reminder_id} data-testid={`reminder-${r.reminder_id}`} className="p-4 flex items-center gap-4">
              <div
                className={`flex-1 min-w-0 ${r.user_id && onNavigateToCandidates ? "cursor-pointer group" : ""}`}
                onClick={() => r.user_id && onNavigateToCandidates?.({ open_user_id: r.user_id })}
                data-testid={`reminder-open-${r.reminder_id}`}
              >
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 ${r.overdue ? "bg-red-100 text-red-700" : r.due_today ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                    {r.overdue ? "En retard" : r.due_today ? "Aujourd'hui" : r.due_date}
                  </span>
                  {r.auto_generated && <Badge className="bg-gray-100 text-gray-600 rounded-none text-[10px]">AUTO</Badge>}
                  {r.reason === "id_doc_expiring_soon" && <Badge className="bg-amber-100 text-amber-700 rounded-none text-[10px]">PIÈCE D&apos;ID. EXPIRE</Badge>}
                  {r.reason === "id_doc_expired" && <Badge className="bg-red-100 text-red-700 rounded-none text-[10px]">PIÈCE D&apos;ID. EXPIRÉE</Badge>}
                  {r.reason === "profile_updated" && <Badge className="bg-purple-100 text-purple-700 rounded-none text-[10px]">PROFIL MODIFIÉ</Badge>}
                  {r.reason === "document_uploaded" && <Badge className="bg-blue-100 text-blue-700 rounded-none text-[10px]">DOC À VALIDER</Badge>}
                  {r.reason === "inactive_30d" && <Badge className="bg-orange-100 text-orange-700 rounded-none text-[10px]">INACTIF 30J</Badge>}
                  {typeof r.reason === "string" && r.reason.startsWith("caces_expiry:") && <Badge className="bg-amber-100 text-amber-700 rounded-none text-[10px]">CACES</Badge>}
                  {r.candidate && <span className="text-xs text-[#0069B4] font-medium group-hover:underline">{r.candidate.name || r.candidate.email}</span>}
                </div>
                <p className="text-sm text-[#3F3F3F]">{r.content}</p>
              </div>
              <div className="flex gap-1">
                {r.status === "pending" && (
                  <button onClick={() => setStatus(r.reminder_id, "done")} className="p-2 text-green-600 hover:bg-green-50" title="Marquer terminé" data-testid={`reminder-done-${r.reminder_id}`}><Check className="w-4 h-4" /></button>
                )}
                <button onClick={() => deleteRem(r.reminder_id)} className="p-2 text-red-500 hover:bg-red-50" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
